import i18next from 'i18next';

const intentBrowserKey = 'wix-connect-browser';
export function wixConnectBrowser() {
  let value = sessionStorage.getItem(intentBrowserKey);
  if (!value) {
    value = Array.from(crypto.getRandomValues(new Uint8Array(32)), b => b.toString(16).padStart(2, '0')).join('');
    sessionStorage.setItem(intentBrowserKey, value);
  }
  return value;
}

export async function wixConnectionRequest(path, body) {
  const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}wix/${path}`, {
    method: 'POST', headers: {'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}`},
    body: JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Wix connection is unavailable. Please retry.');
  return data;
}

export function validWixInstaller(value) {
  const url = new URL(value);
  if (url.origin !== 'https://www.wix.com' || !['/app-installer', '/installer/install'].includes(url.pathname)) {
    throw new Error('Invalid Wix installation destination.');
  }
  return url.href;
}

export async function startWixConnect(botId, options = {}) {
  // Create proof before opening a same-origin popup so its sessionStorage copy
  // contains the browser binding, even when Wix returns into that popup.
  let popup = null;
  try {
    const browser = wixConnectBrowser();
    popup = options.popup ? window.open('about:blank', 'wix-connect', 'width=1000,height=760') : null;
    const current = window.location.pathname + window.location.search;
    const returnPath = options.returnPath || (current.startsWith('/wix') ? `/deploy/channels?bot_id=${encodeURIComponent(botId)}` : current);
    const result = await wixConnectionRequest('connect-intent', {
      bot_id: botId, browser, return_path: returnPath, action: options.action || 'connect',
    });
    if (['activate_widget', 'already_connected'].includes(result.action)) {
      popup?.close();
      if (result.action === 'activate_widget') await wixConnectionRequest(`activate_chatbot?bot_id=${encodeURIComponent(botId)}`, {});
      if (options.onConnected) await options.onConnected();
      return {action: 'connected'};
    }
    if (!['legacy_connect', 'reconnect'].includes(result.action) || !result.url) throw new Error('Invalid Wix connection action.');
    const destination = validWixInstaller(result.url);
    if (popup) popup.location.href = destination;
    else window.location.assign(destination);
    return {...result, popup};
  } catch (error) {
    popup?.close();
    if (options.onError) options.onError(error.message);
    else window.alert(error.message || i18next.t('integrations_connect_init_failed'));
    return {error: error.message};
  }
}

const completions = new Map();
export function completeWixConnect(params) {
  const state = params.get('state') || '';
  if (!state.startsWith('pc_')) return Promise.reject(new Error('Connection must be restarted from your agent.'));
  if (completions.has(state)) return completions.get(state);
  let browser;
  try { browser = wixConnectBrowser(); } catch { return Promise.reject(new Error('Allow browser storage for this portal, then restart the connection.')); }
  const promise = wixConnectionRequest('connect-complete', {
    state, browser, code: params.get('code') || undefined,
    signed_instance: params.get('signedInstance') || params.get('instance') || undefined,
    instance_id: params.get('instanceId') || undefined, app_id: params.get('appId') || undefined,
  });
  completions.set(state, promise);
  promise.finally(() => setTimeout(() => completions.delete(state), 1000)).catch(() => {});
  return promise;
}

export function finishWixConnect(result) {
  const target = result.return_path || '/deploy/channels';
  if (!target.startsWith('/') || target.startsWith('//') || target.includes('\\')) throw new Error('Invalid portal return destination.');
  if (result.bot_id) localStorage.setItem('selectedBotId', result.bot_id);
  if (result.message) window.alert(result.message);
  if (window.opener) {
    window.opener.postMessage({type: 'wix-connect-complete', botId: result.bot_id}, window.location.origin);
    window.close();
  }
  window.location.replace(target);
}
